import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/AuthContext';
import { useServiceCart } from '@/contexts/ServiceCartContext';
import Seo from '@/components/Seo';
import { toast } from 'sonner';
import {
  ArrowLeft, MapPin, Calendar, Clock, Banknote,
  CreditCard, Wallet, CheckCircle2, ShieldCheck
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ServiceCheckoutPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { items, total, clear } = useServiceCart();

  const [step, setStep] = useState(1); // 1=address 2=slot 3=payment
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [form, setForm] = useState({
    contact_name: user?.name || '',
    contact_phone: user?.phone || '',
    address_line1: '',
    address_line2: '',
    city: '',
    pincode: '',
    landmark: '',
    notes: '',
    slot_date: '',
    slot_time: '',
    payment_mode: 'cash_on_service',
  });  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);

  // redirect if empty cart
  useEffect(() => {
    if (items.length === 0 && !submitted) {
      toast.error('Your cart is empty');
      navigate('/services');
    }
  }, [items.length, navigate, submitted]);

  // gentle login gate
  useEffect(() => {
    if (!user) {
      toast.error('Please sign in to checkout');
      navigate('/login', { state: { from: { pathname: '/services/checkout' } } });
    }
  }, [user, navigate]);

  // load slots when date changes
  useEffect(() => {
    if (!form.slot_date) return;
    const load = async () => {
      setLoadingSlots(true);
      try {
        const res = await fetch(`${API}/services/slots/available?slot_date=${form.slot_date}`);
        const data = await res.json();
        setSlots(data.slots || []);
      } finally { setLoadingSlots(false); }
    };
    load();
  }, [form.slot_date]);

  const upd = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const validateStep1 = () => {
    const req = ['contact_name', 'contact_phone', 'address_line1', 'city', 'pincode'];
    for (const k of req) {
      if (!form[k]?.trim()) { toast.error('Please fill all required fields'); return false; }
    }
    if (!/^\d{6}$/.test(form.pincode)) { toast.error('Pincode must be 6 digits'); return false; }
    return true;
  };

  const validateStep2 = () => {
    if (!form.slot_date || !form.slot_time) { toast.error('Pick a date & time slot'); return false; }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateStep1() || !validateStep2()) return;
    setSubmitting(true);
    let popupRef = null;
    try {
      const token = localStorage.getItem('token');
      const payload = {
        items: items.map((i) => ({ service_id: i.service_id, quantity: i.quantity })),
        address_line1: form.address_line1,
        address_line2: form.address_line2,
        city: form.city,
        pincode: form.pincode,
        landmark: form.landmark,
        contact_name: form.contact_name,
        contact_phone: form.contact_phone,
        slot_date: form.slot_date,
        slot_time: form.slot_time,
        payment_mode: form.payment_mode,
        notes: form.notes,
      };

      // If we'll need a popup, pre-open it on the user gesture so popup blockers stay quiet.
      if (form.payment_mode !== 'cash_on_service') {
        popupRef = window.open(
          'about:blank',
          'bharat_estate_pay',
          'width=520,height=720,left=' + Math.max(0, (window.innerWidth - 520) / 2) +
            ',top=' + Math.max(0, (window.innerHeight - 720) / 2) +
            ',resizable=yes,scrollbars=yes'
        );
        if (!popupRef) {
          toast.error('Please allow pop-ups for this site and try again, or pick "Cash on Service".');
          setSubmitting(false);
          return;
        }
        try {
          popupRef.document.write(
            '<html><body style="font-family:system-ui;display:flex;align-items:center;justify-content:center;height:100vh;margin:0;background:#0F172A;color:#D4AF37">' +
            '<div style="text-align:center"><div style="font-size:13px;letter-spacing:.2em;opacity:.7">Bharat Estate</div>' +
            '<div style="font-size:22px;margin-top:8px">Loading secure payment…</div></div></body></html>'
          );
        } catch (_) { /* */ }
      }

      const res = await fetch(`${API}/services/bookings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) {
        try { popupRef && !popupRef.closed && popupRef.close(); } catch (_) { /* */ }
        throw new Error(data.detail || 'Booking failed');
      }

      const bid = data.booking.booking_id;
      setSubmitted(true);

      // If a Razorpay Payment Link came back, redirect popup + poll for payment
      if (data.payment && data.payment.short_url && popupRef && !popupRef.closed) {
        popupRef.location.href = data.payment.short_url;
        const linkId = data.payment.payment_link_id;
        toast.success('Booking created. Complete payment to confirm.');
        // Poll
        let polls = 0;
        const MAX_POLLS = 240;
        const pollTimer = setInterval(async () => {
          polls += 1;
          try {
            const r = await fetch(`${API}/payments/razorpay/order-status/${encodeURIComponent(linkId)}`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            if (r.ok) {
              const s = await r.json();
              if (s.payment_status === 'paid') {
                clearInterval(pollTimer); clearInterval(watcher);
                try { popupRef && !popupRef.closed && popupRef.close(); } catch (_) { /* */ }
                clear();
                navigate(`/services/booking-success?bid=${encodeURIComponent(bid)}`);
                return;
              }
              if (s.payment_status === 'failed') {
                clearInterval(pollTimer); clearInterval(watcher);
                try { popupRef && !popupRef.closed && popupRef.close(); } catch (_) { /* */ }
                toast.error('Payment failed. Your booking is on hold — try again from "My Bookings".');
                clear();
                navigate('/dashboard?tab=bookings');
                return;
              }
            }
          } catch (_) { /* */ }
          if (polls >= MAX_POLLS) {
            clearInterval(pollTimer); clearInterval(watcher);
            setSubmitting(false);
          }
        }, 2000);
        const watcher = setInterval(() => {
          if (popupRef && popupRef.closed) {
            clearInterval(watcher);
            // grace check
            setTimeout(async () => {
              try {
                const r = await fetch(`${API}/payments/razorpay/order-status/${encodeURIComponent(linkId)}`, {
                  headers: { Authorization: `Bearer ${token}` },
                });
                if (r.ok) {
                  const s = await r.json();
                  if (s.payment_status === 'paid') {
                    clearInterval(pollTimer);
                    clear();
                    navigate(`/services/booking-success?bid=${encodeURIComponent(bid)}`);
                    return;
                  }
                }
              } catch (_) { /* */ }
              clearInterval(pollTimer);
              toast.info('Payment incomplete. You can retry from "My Bookings".');
              clear();
              navigate('/dashboard?tab=bookings');
            }, 3000);
          }
        }, 1000);
        return;
      }

      // Cash on service path
      clear();
      toast.success('Booking confirmed!');
      navigate(`/services/booking-success?bid=${encodeURIComponent(bid)}`);
    } catch (e) {
      try { popupRef && !popupRef.closed && popupRef.close(); } catch (_) { /* */ }
      toast.error(e.message || 'Could not book');
    } finally { setSubmitting(false); }
  };

  // date options: next 7 days
  const nextDates = Array.from({ length: 7 }).map((_, i) => {
    const d = new Date();
    d.setDate(d.getDate() + i);
    return d.toISOString().split('T')[0];
  });

  const fmt = (iso) => {
    const d = new Date(iso);
    return { day: d.toLocaleDateString('en-IN', { weekday: 'short' }), date: d.getDate(), month: d.toLocaleDateString('en-IN', { month: 'short' }) };
  };

  return (
    <div className="min-h-screen bg-[#F9F9F7] pb-16" data-testid="checkout-page">
      <Seo title="Checkout · Bharat Estate Home Services" description="Confirm your service booking." path="/services/checkout" />
      <div className="container-luxury max-w-6xl">
        <Link to="/services" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-[#0F172A] mb-6">
          <ArrowLeft className="h-4 w-4" /> Back to services
        </Link>

        {/* Stepper */}
        <div className="flex items-center gap-2 mb-8">
          {[
            { n: 1, l: 'Address' }, { n: 2, l: 'Slot' }, { n: 3, l: 'Confirm' },
          ].map((s, i, arr) => (
            <div key={s.n} className="flex items-center flex-1">
              <div className={`h-8 w-8 rounded-full flex items-center justify-center font-bold text-xs ${step >= s.n ? 'bg-[#D4AF37] text-[#0F172A]' : 'bg-gray-200 text-muted-foreground'}`}>
                {step > s.n ? <CheckCircle2 className="h-4 w-4" /> : s.n}
              </div>
              <p className={`ml-2 text-sm ${step >= s.n ? 'text-[#0F172A] font-semibold' : 'text-muted-foreground'}`}>{s.l}</p>
              {i < arr.length - 1 && <div className={`flex-1 h-px mx-3 ${step > s.n ? 'bg-[#D4AF37]' : 'bg-gray-200'}`} />}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* FORM */}
          <div className="lg:col-span-2 space-y-6">
            {/* STEP 1: Address */}
            {step === 1 && (
              <div className="bg-white p-7 rounded-sm border border-border/50" data-testid="step-address">
                <h2 className="font-['Plus_Jakarta_Sans'] text-2xl font-bold text-[#0F172A] mb-1 flex items-center gap-2"><MapPin className="h-5 w-5 text-[#D4AF37]" /> Service Address</h2>
                <p className="text-sm text-muted-foreground mb-6">Where should our pro arrive?</p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Full Name *</Label>
                    <Input className="mt-1.5" value={form.contact_name} onChange={(e) => upd('contact_name', e.target.value)} placeholder="Your name" data-testid="input-contact-name" />
                  </div>
                  <div>
                    <Label>Phone *</Label>
                    <Input className="mt-1.5" value={form.contact_phone} onChange={(e) => upd('contact_phone', e.target.value)} placeholder="+91 ..." data-testid="input-contact-phone" />
                  </div>
                  <div className="md:col-span-2">
                    <Label>Address Line 1 *</Label>
                    <Input className="mt-1.5" value={form.address_line1} onChange={(e) => upd('address_line1', e.target.value)} placeholder="Flat / House no., building, street" data-testid="input-address1" />
                  </div>
                  <div className="md:col-span-2">
                    <Label>Address Line 2</Label>
                    <Input className="mt-1.5" value={form.address_line2} onChange={(e) => upd('address_line2', e.target.value)} placeholder="Area, locality (optional)" data-testid="input-address2" />
                  </div>
                  <div>
                    <Label>City *</Label>
                    <Input className="mt-1.5" value={form.city} onChange={(e) => upd('city', e.target.value)} placeholder="e.g. Mumbai" data-testid="input-city" />
                  </div>
                  <div>
                    <Label>Pincode *</Label>
                    <Input className="mt-1.5" value={form.pincode} onChange={(e) => upd('pincode', e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="6-digit pincode" data-testid="input-pincode" />
                  </div>
                  <div className="md:col-span-2">
                    <Label>Landmark</Label>
                    <Input className="mt-1.5" value={form.landmark} onChange={(e) => upd('landmark', e.target.value)} placeholder="Near ABC Mall (optional)" />
                  </div>
                  <div className="md:col-span-2">
                    <Label>Special instructions</Label>
                    <textarea className="w-full mt-1.5 rounded-sm border border-input bg-background px-3 py-2 text-sm" rows={2} value={form.notes} onChange={(e) => upd('notes', e.target.value)} placeholder="Any access notes, lift / gate code etc." />
                  </div>
                </div>

                <Button onClick={() => { if (validateStep1()) setStep(2); }} className="mt-6 bg-[#0F172A] text-white hover:bg-[#1E293B] px-7 py-5" data-testid="next-to-slot">
                  Continue to Slot
                </Button>
              </div>
            )}

            {/* STEP 2: Slot */}
            {step === 2 && (
              <div className="bg-white p-7 rounded-sm border border-border/50" data-testid="step-slot">
                <h2 className="font-['Plus_Jakarta_Sans'] text-2xl font-bold text-[#0F172A] mb-1 flex items-center gap-2"><Calendar className="h-5 w-5 text-[#D4AF37]" /> Pick a date & time</h2>
                <p className="text-sm text-muted-foreground mb-6">Slots available in the next 7 days.</p>

                <div className="grid grid-cols-4 md:grid-cols-7 gap-2 mb-6">
                  {nextDates.map((iso) => {
                    const f = fmt(iso);
                    const active = form.slot_date === iso;
                    return (
                      <button key={iso} onClick={() => { upd('slot_date', iso); upd('slot_time', ''); }} className={`p-3 rounded-sm border text-center transition-all ${active ? 'border-[#D4AF37] bg-[#D4AF37]/10 text-[#0F172A]' : 'border-border/50 hover:border-[#D4AF37]/50'}`} data-testid={`date-${iso}`}>
                        <p className="text-xs uppercase text-muted-foreground">{f.day}</p>
                        <p className="font-bold text-lg">{f.date}</p>
                        <p className="text-xs text-muted-foreground">{f.month}</p>
                      </button>
                    );
                  })}
                </div>

                {form.slot_date && (
                  <>
                    <h4 className="font-semibold text-[#0F172A] mb-3 text-sm">Available time slots</h4>
                    {loadingSlots ? (
                      <p className="text-sm text-muted-foreground">Loading slots...</p>
                    ) : (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        {slots.map((s) => {
                          const active = form.slot_time === s.slot_time;
                          return (
                            <button
                              key={s.slot_time}
                              disabled={!s.available}
                              onClick={() => upd('slot_time', s.slot_time)}
                              className={`p-3 rounded-sm border text-sm transition-all ${active ? 'border-[#D4AF37] bg-[#D4AF37]/10 text-[#0F172A] font-semibold' : s.available ? 'border-border/50 hover:border-[#D4AF37]/50 text-[#0F172A]' : 'border-border/30 text-muted-foreground line-through cursor-not-allowed'}`}
                              data-testid={`slot-${s.slot_time.replace(/[: ]/g, '-')}`}
                            >
                              <Clock className="h-3 w-3 inline-block mr-1" /> {s.slot_time}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </>
                )}

                <div className="flex gap-3 mt-8">
                  <Button variant="outline" onClick={() => setStep(1)} className="px-5">Back</Button>
                  <Button onClick={() => { if (validateStep2()) setStep(3); }} className="bg-[#0F172A] text-white hover:bg-[#1E293B] px-7 py-5" data-testid="next-to-payment">
                    Continue to Payment
                  </Button>
                </div>
              </div>
            )}

            {/* STEP 3: Confirm */}
            {step === 3 && (
              <div className="bg-white p-7 rounded-sm border border-border/50" data-testid="step-payment">
                <h2 className="font-['Plus_Jakarta_Sans'] text-2xl font-bold text-[#0F172A] mb-1 flex items-center gap-2"><Wallet className="h-5 w-5 text-[#D4AF37]" /> Confirm your booking</h2>
                <p className="text-sm text-muted-foreground mb-6">Almost there — review and submit.</p>

                <div className="rounded-sm border-2 border-[#D4AF37] bg-[#D4AF37]/5 p-5 flex items-start gap-4">
                  <div className="h-10 w-10 rounded-sm bg-[#D4AF37] text-[#0F172A] flex items-center justify-center flex-shrink-0">
                    <Banknote className="h-5 w-5" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-[#0F172A]">Cash on Service · No charge now</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Our pro will call you to confirm requirements and share a tailored quote.
                      You only pay after the job is done — directly to the pro, in cash or UPI.
                    </p>
                  </div>
                </div>

                <div className="flex gap-3 mt-8">
                  <Button variant="outline" onClick={() => setStep(2)} className="px-5">Back</Button>
                  <Button onClick={handleSubmit} disabled={submitting} className="bg-[#D4AF37] text-[#0F172A] hover:bg-[#B8932F] font-bold px-7 py-5 flex-1" data-testid="confirm-booking-btn">
                    {submitting ? 'Submitting...' : 'Request Booking'}
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* ORDER SUMMARY */}
          <aside>
            <div className="bg-white border border-border/50 rounded-sm p-6 sticky top-24" data-testid="order-summary">
              <h3 className="font-['Plus_Jakarta_Sans'] text-xl font-bold text-[#0F172A] mb-4">Order Summary</h3>
              <div className="space-y-3 mb-4 max-h-64 overflow-y-auto">
                {items.map((i) => (
                  <div key={i.service_id} className="flex gap-3">
                    <img src={i.image} alt={i.name} className="h-12 w-12 object-cover rounded-sm flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-[#0F172A] truncate">{i.name}</p>
                      <p className="text-xs text-muted-foreground">Qty {i.quantity}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="border-t border-border/50 pt-3 text-sm space-y-2">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Service fee</span>
                  <span className="text-[#10B981] font-semibold">Free</span>
                </div>
                <p className="text-xs text-muted-foreground bg-[#F9F9F7] rounded-sm p-3 mt-3 leading-relaxed">
                  Our pro will call you to confirm requirements and share a tailored quote.
                  You only pay <span className="font-semibold text-[#0F172A]">after the job is done</span>.
                </p>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default ServiceCheckoutPage;
